import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, CalendarDays, Swords, MessageCircle, User, ArrowLeftRight, GraduationCap } from "lucide-react";
import { cn } from "@/lib/utils";
import { isNavActive } from "@/lib/navActive";
import { useAuth } from "@/hooks/useAuth";
import { useTotalUnreadCount } from "@/hooks/useTotalUnreadCount";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useUserRole } from "@/hooks/useUserRole";
import { useAppMode } from "@/hooks/useAppMode";
import { useTranslation } from "react-i18next";

// 10a/G-093: same feature keys per item as DesktopSidebar — the gate set must
// not change with the breakpoint (Hem/Profil are intentionally ungated in both).
const navItems = [
  { to: "/", icon: Home, labelKey: "nav.home" },
  { to: "/book", icon: CalendarDays, labelKey: "nav.book", feature: "bookings" },
  { to: "/play", icon: Swords, labelKey: "nav.play", feature: "open_matches" },
  { to: "/messages", icon: MessageCircle, labelKey: "nav.chat", feature: "chat" },
  { to: "/profile", icon: User, labelKey: "nav.profile" },
];

export default function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isEnabled } = useFeatureFlags();
  const { isAdmin, roles } = useUserRole();
  const { setMode } = useAppMode();
  const { t } = useTranslation("common");

  const unreadCount = useTotalUnreadCount();

  const visibleItems = navItems.filter((item) => !item.feature || isEnabled(item.feature));

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 border-t bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 shadow-nav lg:hidden">
      <div className="flex h-16 items-center justify-around px-2">
        {visibleItems.map(({ to, icon: Icon, labelKey }) => {
          const active = isNavActive(location.pathname, to);
          return (
            <Link
              key={to}
              to={to}
              className={cn(
                "flex flex-col items-center gap-0.5 px-3 py-1 text-xs transition-colors relative",
                active ? "text-primary" : "text-muted-foreground hover:text-foreground"
              )}
            >
              <div className="relative">
                <Icon className="h-5 w-5" />
                {to === "/messages" && unreadCount > 0 && (
                  <span
                    className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive text-[8px] font-bold text-destructive-foreground px-0.5"
                    aria-label={t("inbox.unreadCount", { count: unreadCount })}
                  >
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </div>
              <span>{t(labelKey)}</span>
              {active && (
                <span className="absolute -top-1 left-1/2 -translate-x-1/2 h-1 w-6 rounded-full bg-primary" />
              )}
            </Link>
          );
        })}
        {roles.includes("coach") && (
          <Link
            to="/coach"
            className={cn(
              "flex flex-col items-center gap-0.5 px-3 py-1 text-xs transition-colors relative",
              location.pathname.startsWith("/coach") ? "text-primary" : "text-muted-foreground hover:text-foreground"
            )}
          >
            <GraduationCap className="h-5 w-5" />
            <span>{t("nav.coach")}</span>
            {location.pathname.startsWith("/coach") && (
              <span className="absolute -top-1 left-1/2 -translate-x-1/2 h-1 w-6 rounded-full bg-primary" />
            )}
          </Link>
        )}
        {isAdmin && (
          <button
            onClick={() => { setMode("manager"); navigate("/manager/dashboard"); }}
            className="flex flex-col items-center gap-0.5 px-3 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeftRight className="h-5 w-5" />
            <span>Admin</span>
          </button>
        )}
      </div>
    </nav>
  );
}
